import { FJDecoderManager } from '@fj/codec';
import FJConfig from '../../config/FJConfig';
import FJConstant from '../../constants/FJConstant';
import FJUtil from '../../util/FJUtil';
import FJVideoUtil from '../../util/FJVideoUtil';
import {
    setSliceAudioWavesurferUrl,
    setSliceIsUploadFileLoading,
    setSlicePresetList,
    setSlicePresetListStatus,
    setSliceProgress,
    setSlicePrompts,
    setSliceResultAudioInfo,
    setSliceResultAudioInfoStatus,
    setSliceResultInfo,
    setSliceThumbnailUrl,
    setSliceTrimInfo,
    setSliceTrimModelInfo,
    setSliceTrimVideoInfo,
    setVideoInfo,
} from '../slices/FJSliceAiVideoDub';
import FJActionModal from './FJActionModal';
import { setSliceIsDoneAction, setSliceTaskStatusAction } from '../slices/FJSliceCommon';
import FJPoolStore from '../../store/FJPoolStore';
import FJImageStore from '../../store/FJImageStore';
import FJNetworkStore from '../../store/FJNetworkStore';
import FJS3Config from '../../config/FJS3Config';
import FJActionCommon from './FJActionCommon';
import FJProgressUtil from '../../util/FJProgressUtil';
import FJCodecStore from '../../store/FJCodecStore';
import FJLocalStore from '../../store/FJLocalStore';
import FJMessageStore from '../../store/FJMessageStore';
import FJLoadLocalforageStore from '../../store/FJLoadLocalforageStore';

// 弹出裁剪视频弹窗的时长
export const TRIM_VIDEO_DURATION = 20;

// 模型支持的视频最大时长
export const MODEL_MAX_VIDEO_DURATION = 60;

// 上传支持的最大时长
export const UPLOAD_MAX_VIDEO_DURATION = 60 * 60;

// 视频最小时长
export const UPLOAD_MIN_VIDEO_DURATION = 1;

export const UPLOAD_FILE_TYPE_OF_VIDEO_DUB = [
    '.mdv',
    '.mp4',
    '.avi',
    '.mov',
    '.mpeg',
    '.m4v',
    '.webm',
    '.mkv',
    '.ogg',
    '.3g2',
];

// 当前任务ID 用来取消任务
let _currentEventId = null;

// 生成任务ID  用来判断是否中断
let _generateEventId = null;

// 是否正在生成
let _isGenerating = false;
// 生成数据
let _generateData = null;

const _checkVideoFile = async file => {
    const fileType = file.name.split('.').pop().toLowerCase();

    if (!UPLOAD_FILE_TYPE_OF_VIDEO_DUB.includes(`.${fileType}`)) {
        return {
            status: false,
            errorType: 'video-file-type-error',
        };
    }

    try {
        const videoInfo = await FJVideoUtil.loadVideo(file);

        if (!videoInfo) {
            return {
                status: false,
                errorType: 'video-file-load-error',
            };
        }

        if (videoInfo.duration < UPLOAD_MIN_VIDEO_DURATION) {
            return {
                status: false,
                errorType: 'video-duration-too-short',
            };
        }

        if (videoInfo.duration > UPLOAD_MAX_VIDEO_DURATION) {
            return {
                status: false,
                errorType: 'video-duration-too-long',
            };
        }

        if (videoInfo.width === 0 || videoInfo.height === 0) {
            return {
                status: false,
                errorType: 'video-need-transcoded',
            };
        }

        return {
            status: true,
            videoInfo,
        };
    } catch (e) {
        return {
            status: false,
            errorType: 'video-file-load-error',
        };
    }
};

const _messageErrorOfCheckVideoFile = errorType => {
    switch (errorType) {
        case 'video-file-load-error':
        case 'video-file-type-error':
            return FJMessageStore.addMessage(FJLocalStore._('unsupported-format-ai-video-dub'), 'error');
        case 'video-duration-too-short':
            return FJMessageStore.addMessage(
                FJLocalStore._('please-upload-a-video-of-more-than-xxx').replace(
                    'xxx',
                    UPLOAD_MIN_VIDEO_DURATION + 's',
                ),
                'error',
            );
        case 'video-duration-too-long':
            return FJMessageStore.addMessage(
                FJLocalStore._('please-upload-a-video-that-is-less-than-xxx-hour-long').replace(
                    'xxx',
                    Math.floor(UPLOAD_MAX_VIDEO_DURATION / 3600),
                ),
                'error',
            );
        case 'video-need-transcoded':
            return FJMessageStore.addMessage(
                FJLocalStore._('this-video-needs-to-be-transcoded-please-upload-another-video'),
                'error',
            );
        default:
            return FJMessageStore.addMessage(FJLocalStore._('unsupported-format-ai-video-dub'), 'error');
    }
};

const __getThumbnailUrl = async (videoSrc, currentTime = 0) => {
    const video = document.createElement('video');
    video.src = videoSrc;
    video.currentTime = currentTime;

    const canvas = FJPoolStore.getCanvas();
    const ctx = canvas.getContext('2d');

    await new Promise(resolve => {
        video.addEventListener('loadeddata', () => {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            resolve();
        });
    });

    video.currentTime = 0;
    await new Promise(resolve => {
        video.addEventListener('seeked', () => {
            ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
            resolve();
        });
    });

    const thumbnailUrl = canvas.toDataURL('image/jpeg');
    FJPoolStore.releaseCanvas(canvas);
    return thumbnailUrl;
};

const _getExportSize = size => {
    let exportSize = { width: size.width, height: size.height };

    if (size.width > 1080 && size.height > 1080) {
        if (size.width > size.height) {
            exportSize.height = 1080;
            exportSize.width = size.width * (1080 / size.height);
        } else {
            exportSize.width = 1080;
            exportSize.height = size.height * (1080 / size.width);
        }
    }
    return {
        width: Math.round(exportSize.width),
        height: Math.round(exportSize.height),
    };
};

const FJActionAiVideoDub = {
    /**
     * @description 错误回调
     */
    _errorCallback: function (isFree = false) {
        return dispatch => {
            _isGenerating = false;
            FJProgressUtil.endProgress();
            dispatch(setSliceTaskStatusAction(FJConstant.ERROR));
            FJUtil.updateAllCreditInfo(isFree);
        };
    },

    /**
     * @description 成功回调
     */
    _successCallback: function (finishTaskCreationList = []) {
        return dispatch => {
            const videoInfo = _generateData.videoInfo;
            const trimInfo = _generateData.trimInfo;

            _isGenerating = false;
            _currentEventId = null;
            const item = finishTaskCreationList[0];

            FJProgressUtil.endProgress();
            if (item) {
                dispatch(
                    setSliceResultInfo({
                        videoUrl: item.videoUrl,
                        width: videoInfo.width,
                        height: videoInfo.height,
                        duration: trimInfo.end - trimInfo.start,
                    }),
                );
                dispatch(this.separateAudioAction(item.videoUrl));
            } else {
                this._errorCallback();
            }
        };
    },

    /**
     * @description 轮询任务回调
     */
    _pollingTaskCallbackAction: function (data) {
        return dispatch => {
            const { type } = data;

            if (type === FJConstant.SUCCESS) {
                const finishTaskCreationList = data.finishTaskCreationList;
                dispatch(this._successCallback(finishTaskCreationList));

                // let _endTime = new Date().getTime();
                // const generatingDuration = FJUtil.getTimeDiff(waitingEndTime, _endTime);

                // FJUtil.eaLog('ai video bg remover success', {
                //     'generating duration': generatingDuration,
                //     type: 'auto',
                //     'credit&free': isFree ? 'free' : 'credit',
                //     'video duration': FJUtil.getTimeDiff(0, editDuration * 1000),
                // });

                FJUtil.eaLog('ai tool generate success', {
                    tool: FJUtil.getToolName(),
                    'credit&free': _generateData.isFree ? 'free' : 'credit',
                });

                if (_generateData) {
                    const isTrim =
                        _generateData.trimInfo.start > 0 ||
                        _generateData.trimInfo.end < _generateData.videoInfo.duration;

                    FJUtil.eaLog('video to sfx generator success', {
                        description: _generateData.prompt,
                        'remove original audio': _generateData.removeOriginAudio,
                        'credit&free': _generateData.isFree ? 'free' : 'credit',
                        'trim video': isTrim,
                        'video duration': _generateData.trimInfo.end - _generateData.trimInfo.start,
                        'video original duration': _generateData.videoInfo.duration,
                    });
                }
            }

            if (type === FJConstant.ERROR) {
                dispatch(this._errorCallback());
            }
        };
    },

    /**
     * @description 上传视频
     * @param {File} file
     * @returns
     */
    uploadVideoAction: function (file, resource = 'local', originUrl = null) {
        return async dispatch => {
            dispatch(setSliceIsUploadFileLoading(true));
            const { status, videoInfo, errorType } = await _checkVideoFile(file);

            if (!status) {
                _messageErrorOfCheckVideoFile(errorType);
                dispatch(setSliceIsUploadFileLoading(false));
                return;
            }

            dispatch(
                setSliceTrimVideoInfo({
                    ...videoInfo,
                    originUrl,
                    resource: resource,
                }),
            );

            if (videoInfo.duration > TRIM_VIDEO_DURATION) {
                // 弹出裁剪弹窗
                dispatch(
                    setSliceTrimModelInfo({
                        start: 0,
                        end:
                            videoInfo.duration > MODEL_MAX_VIDEO_DURATION
                                ? MODEL_MAX_VIDEO_DURATION
                                : videoInfo.duration,
                    }),
                );
                dispatch(FJActionAiVideoDub.enterTrimVideoAction(true));
                dispatch(setSliceIsUploadFileLoading(false));
                return;
            }

            dispatch(setSliceTrimInfo({ start: 0, end: videoInfo.duration }));
            dispatch(setSliceTrimModelInfo({ start: 0, end: videoInfo.duration }));
            dispatch(setVideoInfo({ ...videoInfo, originUrl, resource }));
            dispatch(setSliceIsUploadFileLoading(false));

            FJUtil.eaLog('video to sfx generator video resource', {
                resource: resource,
            });
        };
    },

    /**
     * @description 删除视频
     */
    deleteVideoAction: function () {
        return async dispatch => {
            dispatch(setSliceTrimInfo({}));
            dispatch(setSliceTrimModelInfo({}));
            dispatch(setVideoInfo({}));
        };
    },

    /**
     * @description 应用裁剪视频  假裁剪
     */
    applyTrimVideoAction: function (trimBoxRelativeRect, callback) {
        return async (dispatch, getState) => {
            const trimVideoInfo = getState().aiVideoDub.trimVideoInfo;

            if (!trimVideoInfo) {
                callback && callback();
                return;
            }

            const currentTrimInfo = getState().aiVideoDub.trimInfo;
            const videoInfo = getState().aiVideoDub.videoInfo;

            const newTrimInfo = {
                start: Number((trimVideoInfo.duration * trimBoxRelativeRect.left).toFixed(2)),
                end: Number(
                    (trimVideoInfo.duration * (trimBoxRelativeRect.left + trimBoxRelativeRect.width)).toFixed(2),
                ),
            };

            if (
                FJUtil.compareTimes(newTrimInfo.start, currentTrimInfo.start) &&
                FJUtil.compareTimes(newTrimInfo.end, currentTrimInfo.end) &&
                trimVideoInfo.src === videoInfo.src
            ) {
                callback && callback();
                dispatch(FJActionModal.hideVideoTrimModalAction());
                return;
            }

            dispatch(
                setVideoInfo({
                    ...trimVideoInfo,
                    start: newTrimInfo.start,
                    end: newTrimInfo.end,
                }),
            );
            dispatch(setSliceTrimInfo({ start: newTrimInfo.start, end: newTrimInfo.end }));
            dispatch(setSliceTrimModelInfo({ start: newTrimInfo.start, end: newTrimInfo.end }));
            callback && callback();
            dispatch(FJActionModal.hideVideoTrimModalAction());
        };
    },

    /**
     * @description 进入裁剪视频弹窗
     */
    enterTrimVideoAction: function (useTrimVideoInfo = false) {
        return async (dispatch, getState) => {
            const trimVideoInfo = getState().aiVideoDub.trimVideoInfo;
            const videoInfo = getState().aiVideoDub.videoInfo;

            const trimModelInfo = getState().aiVideoDub.trimModelInfo;

            const trimInfo = useTrimVideoInfo ? { ...videoInfo, ...trimVideoInfo } : { ...trimVideoInfo, ...videoInfo };

            dispatch(FJActionModal.showVideoTrimModalAction({ ...trimInfo, ...trimModelInfo }));

            FJUtil.eaLog('video to sfx trim window show');
        };
    },

    /**
     * @description 开始生成
     */
    generateAction: function (generateData, isRetry = false) {
        return async (dispatch, getState) => {
            const videoInfo = generateData?.videoInfo || getState().aiVideoDub.videoInfo;
            const prompt = generateData?.prompt || getState().aiVideoDub.prompts;
            const generateAllTime = generateData?.generateAllTime || getState().aiVideoDub.generateAllTime;
            const trimInfo = generateData?.trimInfo || getState().aiVideoDub.trimInfo;
            const useCredit = generateData?.useCredit || getState().aiVideoDub.useCredit;
            const removeOriginAudio = generateData?.removeOriginAudio || getState().aiVideoDub.removeOriginAudio;
            _generateEventId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            let currentGenerateId = _generateEventId;

            if (!isRetry) {
                FJUtil.eaLog('video to sfx generator click');
            }

            if (!videoInfo || Object.keys(videoInfo).length === 0 || !prompt) {
                FJMessageStore.addMessage(
                    FJLocalStore._('please-upload-a-video-or-describe-the-video-content'),
                    'error',
                );
                return;
            }

            if (FJUtil.isLocalhost()) {
                dispatch(
                    setSliceResultInfo({
                        videoUrl: videoInfo.src,
                        width: videoInfo.width,
                        height: videoInfo.height,
                        duration: videoInfo.duration,
                    }),
                );
                dispatch(setSliceTaskStatusAction(FJConstant.SUCCESS));
                dispatch(this.separateAudioAction(videoInfo.src));
                return;
            }

            if (!videoInfo) {
                return;
            }

            const __check = eventId => {
                if ((eventId && eventId !== _generateEventId) || !_isGenerating) {
                    throw new Error('cancel');
                }
            };

            let isFree = false;
            // 登录判断
            const __judgeCanGenerate = async () => {
                // 登录判断
                const userSpaceType = await FJUtil.checkUserSpace({
                    errorCallback: () => {
                        FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), 'error');
                    },
                });
                if (userSpaceType > 0) {
                    dispatch(FJActionCommon.userSpaceChangeAction(userSpaceType));
                    return false;
                }
                // 额度判断
                const { creditNumber, freeNumCreation } = getState().common;
                const canCredit = creditNumber >= useCredit;
                const useFree = !canCredit && freeNumCreation > 0;
                isFree = useFree;

                if (!canCredit && !useFree) {
                    dispatch(FJActionModal.showUpgradeModalAction());
                    return false;
                }
                return true;
            };

            try {
                _isGenerating = true;
                const canGenerate = await __judgeCanGenerate();
                __check(currentGenerateId);
                if (!canGenerate) {
                    // 如果是重试，则进行错误回调
                    if (generateData) {
                        dispatch(this._errorCallback(isFree));
                    }
                    _isGenerating = false;
                    return;
                }

                _generateData = {
                    videoInfo: { ...videoInfo },
                    prompt,
                    generateAllTime,
                    trimInfo,
                    useCredit,
                    removeOriginAudio,
                    isFree,
                };

                dispatch(setSliceIsDoneAction(false));
                dispatch(setSliceProgress(0));
                dispatch(setSliceTaskStatusAction(FJConstant.START));
                dispatch(FJActionCommon.setHeaderTabIndexAction(0));

                const thumbnailUrl = await __getThumbnailUrl(videoInfo.src, trimInfo.start);
                __check(currentGenerateId);
                dispatch(setSliceThumbnailUrl(thumbnailUrl));

                const isTrim = trimInfo.start > 0 || trimInfo.end < videoInfo.duration;
                const exportSize = _getExportSize({ width: videoInfo.width, height: videoInfo.height });
                const isCompress = videoInfo.width !== exportSize.width;

                let trimVideoSource = null;
                if (isTrim || isCompress) {
                    const blob = await fetch(videoInfo.src).then(res => res.blob());

                    // 裁剪视频
                    trimVideoSource = await new Promise(resolve => {
                        const __trimVideoComplete = blob => {
                            resolve(new File([blob], videoInfo.name, { type: 'video/mp4' }));
                        };
                        const __trimVideoError = () => {
                            resolve();
                        };

                        const __trimVideoProgress = progress => {
                            dispatch(setSliceProgress(Math.round(progress * 100 * 0.5)));
                        };

                        FJCodecStore.trimVideoAndExport(blob, {
                            start: trimInfo.start,
                            end: trimInfo.end,
                            isCompress: isCompress,
                            size: exportSize,
                            onComplete: __trimVideoComplete,
                            onError: __trimVideoError,
                            onProgress: __trimVideoProgress,
                        });
                    });
                } else {
                    trimVideoSource = await fetch(videoInfo.src).then(res => res.blob());
                }
                if (!trimVideoSource) {
                    dispatch(this._errorCallback(isFree));
                    return;
                }

                // 上传文件
                let originUrl = videoInfo.originUrl;
                if (!originUrl || isTrim || isCompress) {
                    const key = await FJUtil.hashEncode(trimVideoSource);
                    const fileExtension = videoInfo.name.split('.').pop();
                    const s3Url = FJConfig.originalImageKey + `${key}.${fileExtension}`;

                    originUrl = FJS3Config.S3_SERVER_HOST + s3Url;
                    const s3Res = await FJImageStore.upLoadFileToS3Async(trimVideoSource, s3Url);

                    __check(currentGenerateId);

                    if (!s3Res) {
                        dispatch(this._errorCallback(isFree));
                        return;
                    }
                }

                const resultVideoSize = isCompress ? exportSize : { width: videoInfo.width, height: videoInfo.height };

                const _request = async () => {
                    return await FJNetworkStore.createVideoDubTask({
                        url: originUrl,
                        prompt: prompt,
                        duration: trimInfo.end - trimInfo.start,
                        width: resultVideoSize.width,
                        height: resultVideoSize.height,
                        removeOriginAudio: removeOriginAudio,
                        resource: isTrim || isCompress ? 'local' : videoInfo.resource,
                    });
                };

                // 创建任务
                const currentTaskId = await FJActionCommon.createRequestTask(_request, dispatch);

                FJUtil.eaLog(' ai tool start generating', {
                    tool: FJUtil.getToolName(),
                });

                FJUtil.updateAllCreditInfo(isFree);

                if (!currentTaskId) {
                    dispatch(this._errorCallback(isFree));
                    return;
                }
                _currentEventId = currentTaskId;

                _generateEventId = _currentEventId;
                currentGenerateId = _currentEventId;

                __check(currentGenerateId);

                // 上传缩略图
                const thumbnailBlob = await fetch(thumbnailUrl).then(res => res.blob());
                const thumbnailS3Url = FJConfig.resultCoverKey + `${currentGenerateId}.png`;
                __check(currentGenerateId);
                const thumbnailS3Res = await FJImageStore.upLoadFileToS3Async(thumbnailBlob, thumbnailS3Url);
                __check(currentGenerateId);

                if (!thumbnailS3Res) {
                    // 取消任务
                    dispatch(FJActionCommon.cancelTaskAction([_currentEventId], false));
                    // 错误回调
                    dispatch(this._errorCallback(isFree));
                    return;
                }

                // 3. 轮询任务状态
                const __callback = data => {
                    dispatch(this._pollingTaskCallbackAction(data, isFree));
                };

                // 开始进度
                FJProgressUtil.startProgress(0, generateAllTime, progress => {
                    dispatch(setSliceProgress(isTrim || isCompress ? 50 + progress * 0.5 : progress));
                });

                dispatch(
                    FJActionCommon.pollingTaskAction([_currentEventId], {
                        callback: __callback,
                    }),
                );
            } catch (error) {
                if (error.message === 'cancel') {
                    _currentEventId = null;
                    return;
                }
                _isGenerating = false;
                dispatch(this._errorCallback(isFree));
            }
        };
    },

    /**
     * @description 分离音频
     */
    separateAudioAction: function (videoUrl) {
        return async (dispatch, getState) => {
            const videoInfo = getState().aiVideoDub.videoInfo;
            const resultInfo = getState().aiVideoDub.resultInfo;

            let resultVideoUrl = videoUrl || resultInfo.videoUrl;

            if (!resultVideoUrl) {
                dispatch(setSliceResultAudioInfoStatus(FJConstant.ERROR));
                return;
            }

            dispatch(setSliceResultAudioInfoStatus(FJConstant.LOADING));
            try {
                const blob = await fetch(resultVideoUrl).then(res => res.blob());
                const audioData = await FJDecoderManager.decodeAudioOverall(blob, {
                    start: 0,
                    end: videoInfo.duration,
                    samplerate: FJUtil.getAudioSampleRate(),
                    mode: 'FFMPEG',
                    speed: 1,
                    fileType: 'video',
                    ignoreNoValidDuration: true,
                });

                if (!audioData || audioData.length === 0) {
                    throw new Error('audio-data-error');
                }

                const wavBuffer = await FJUtil.encodeMp3(audioData);
                if (wavBuffer) {
                    const mimeType = 'audio/mpeg';
                    const blob = new Blob(wavBuffer, { type: mimeType });
                    const audioFile = new File([blob], 'audio.mp3', { type: mimeType });
                    const audioUrl = URL.createObjectURL(audioFile);
                    dispatch(
                        setSliceResultAudioInfo({
                            audioUrl,
                            duration: videoInfo.duration,
                        }),
                    );
                } else {
                    throw new Error('audio-data-error');
                }

                dispatch(setSliceResultAudioInfoStatus(FJConstant.SUCCESS));
            } catch (error) {
                dispatch(setSliceResultAudioInfoStatus(FJConstant.ERROR));
            }
        };
    },

    /**
     * @description 更新音频波形图
     */
    updateAudioWavesurferUrlAction: function (size) {
        return async (dispatch, getState) => {
            const audioUrl = getState().aiVideoDub.resultAudioInfo.audioUrl;

            if (!audioUrl) {
                return;
            }

            const audioWavesurferUrl = await FJUtil.getAudioWave(audioUrl, false, {
                width: size.width,
                height: size.height,
                waveColor: '#3d9c7b',
            });
            dispatch(setSliceAudioWavesurferUrl(audioWavesurferUrl));
        };
    },

    /**
     * @description 初始化预设列表
     */
    _initPresetAIVideoDubAction: function () {
        return async (dispatch, getState) => {
            const presetListStatus = getState().aiVideoDub.presetListStatus;

            if (presetListStatus === FJConstant.LOADING || presetListStatus === FJConstant.SUCCESS) {
                return;
            }

            dispatch(setSlicePresetListStatus(FJConstant.LOADING));
            try {
                const response = await fetch(FJConfig.presetListJsonPath);
                const { list: presetList } = await response.json();
                const usePresetList = presetList.map(item => {
                    return {
                        coverUrl: FJConfig.presetPreviewPath + 'img/' + item.name + '.png',
                        videoUrl: window.location.origin + FJConfig.presetPreviewPath + 'video/' + item.name + '.mp4',
                        prompt: item.prompt,
                    };
                });

                dispatch(setSlicePresetList(usePresetList));
                dispatch(setSlicePresetListStatus(FJConstant.SUCCESS));
            } catch (error) {
                dispatch(setSlicePresetListStatus(FJConstant.ERROR));
            }
        };
    },

    initAIVideoDubAction: function () {
        return async dispatch => {
            dispatch(this._initPresetAIVideoDubAction());
            dispatch(this._initAIVideoDubProjectAction());
        };
    },

    /**
     * @description 生成同类型视频
     * @param {Object} item
     */
    generateSameKindAction: function (item, callback) {
        return async dispatch => {
            try {
                const blob = await fetch(item.videoUrl).then(res => res.blob());
                const file = new File([blob], 'Video.mp4', { type: 'video/mp4' });
                await dispatch(this.uploadVideoAction(file, 'preset', item.videoUrl));

                dispatch(setSlicePrompts(item.prompt));
            } catch (error) {
                FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), 'error');
            } finally {
                callback && callback();
            }
        };
    },

    /**
     * @description 重试
     */
    retryAction: function () {
        return async (dispatch, getState) => {
            const __reloadTask = async () => {
                if (!_generateData) {
                    return;
                }
                dispatch(setSliceProgress(0));
                dispatch(setSliceTaskStatusAction(FJConstant.START));
                dispatch(this.generateAction(_generateData, true));
            };

            const __processTask = async () => {
                const curProgress = getState().aiVideoDub.progress;
                FJProgressUtil.startProgress(curProgress, _generateData?.generateAllTime, progress => {
                    dispatch(setSliceProgress(progress));
                });
                dispatch(setSliceTaskStatusAction(FJConstant.GENERATING));

                // 3. 轮询任务状态
                const __callback = data => {
                    const { type } = data;

                    if (type === FJConstant.SUCCESS) {
                        const finishTaskCreationList = data.finishTaskCreationList;
                        dispatch(this._successCallback(finishTaskCreationList));
                    }

                    if (type === FJConstant.ERROR) {
                        dispatch(this._errorCallback());
                    }
                };

                dispatch(
                    FJActionCommon.pollingTaskAction([_currentEventId], {
                        callback: __callback,
                    }),
                );
            };

            let successCallbackData = null;

            if (!_currentEventId) {
                __reloadTask();
            } else {
                try {
                    const status = await FJUtil.checkTaskStatus(result => {
                        if (result) {
                            successCallbackData = result;
                        }
                    }, _currentEventId);

                    switch (status) {
                        case 'succeeded':
                            dispatch(this._successCallback(successCallbackData));
                            dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
                            break;
                        case 'processing':
                            __processTask();
                            break;
                        case 'failed':
                            __reloadTask();
                            break;
                        case 'error':
                            FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), 'error');
                            dispatch(this._errorCallback());
                            break;
                    }
                } catch (error) {
                    dispatch(this._errorCallback());
                }
            }
        };
    },

    /**
     * @description 取消
     */
    cancelAction: function () {
        return async (dispatch, getState) => {
            const resultInfo = getState().aiVideoDub.resultInfo;

            try {
                _isGenerating = false;
                if (_currentEventId) {
                    await FJNetworkStore.cancelTask(_currentEventId);
                }
                FJProgressUtil.endProgress();
                dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
                const isSuccess = resultInfo && resultInfo.videoUrl;
                dispatch(setSliceIsDoneAction(isSuccess));
                _currentEventId = null;
            } catch (e) {
                FJMessageStore.addMessage(FJLocalStore._('cancel-to-delete-please-try-again'), 'error');
            }
        };
    },

    /**
     * @description 使用图片
     * @param {Object} info
     */
    useAIImageAction: function (info) {
        return async dispatch => {
            try {
                const blob = await fetch(info.videoUrl).then(res => res.blob());

                const isTextToVideo = info.generationType === 'tv';

                const file = new File([blob], 'Video.mp4', { type: 'video/mp4' });
                await dispatch(
                    this.uploadVideoAction(file, isTextToVideo ? 'text to video' : 'image to video', info.videoUrl),
                );
            } catch (error) {
                FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), 'error');
            }
        };
    },

    /**
     * @desc 初始化图片去背景项目
     */
    _initAIVideoDubProjectAction: function () {
        return async dispatch => {
            const recId = FJUtil.getQueryString(FJConstant.REC_ID);
            const recIdInfo = recId ? JSON.parse(window.localStorage.getItem(recId)) : null;
            if (recIdInfo) {
                const url = location.origin + location.pathname;
                FJUtil.changeLocation(url);
                window.localStorage.removeItem(recId);

                const { prompt, videoUrl, fileList, avator } = recIdInfo;

                if (fileList) {
                    const fileInfo = fileList[0]?.info;
                    if (!fileInfo) return;
                    const data = await FJLoadLocalforageStore.getItem(fileList[0].key);
                    const file = new File([data.data], fileInfo.name, { type: fileInfo.type });
                    dispatch(this.uploadVideoAction(file));
                } else if (avator) {
                    try {
                        dispatch(setSliceIsUploadFileLoading(true));
                        const blob = await FJUtil.getBlobByUrl(avator);
                        const fileName = avator.split('/').pop() || 'Video.mp4';
                        const file = new File([blob], fileName, { type: 'video/mp4' });
                        dispatch(this.uploadVideoAction(file));
                    } catch (error) {
                        dispatch(setSliceIsUploadFileLoading(false));
                    }
                } else if (videoUrl) {
                    try {
                        dispatch(setSliceIsUploadFileLoading(true));
                        prompt && dispatch(setSlicePrompts(prompt));
                        const blob = await FJUtil.getBlobByUrl(videoUrl);
                        const fullFileName = videoUrl.split('/').pop();
                        const lastDotIndex = fullFileName.lastIndexOf('.');
                        // 提取最后一个点之后的部分作为后缀名，如果找不到点则后缀为空字符串
                        const extension = lastDotIndex > -1 ? fullFileName.substring(lastDotIndex + 1) : 'mp4';
                        const fileName = 'Video.' + extension;
                        const file = new File([blob], fileName, { type: 'video/mp4' });

                        dispatch(this.uploadVideoAction(file));
                    } catch (error) {
                        dispatch(setSliceIsUploadFileLoading(false));
                    }
                }
            }
        };
    },
};

export default FJActionAiVideoDub;
